import React, { ReactNode } from 'react';
import ListItem from '@mui/material/ListItem';
import { Fonts, MenuStyle } from '@crema/constants/AppEnums';
import { alpha } from '@mui/material';
import { useSidebarContext } from '@crema/context/SidebarContextProvider';
import clsx from 'clsx';

type VerticalCollapseItemProps = {
  children: ReactNode;
  sidebarTextColor: string;
  level: number;
  [x: string]: any;
};

const VerticalCollapseItem: React.FC<VerticalCollapseItemProps> = ({ children, sidebarTextColor, level, ...rest }) => {
  const { sidebarMenuSelectedBgColor, sidebarMenuSelectedTextColor, menuStyle } = useSidebarContext();
  const { component, ...others } = rest;
  const isDomComponent = !component || typeof component === 'string';
  const { activeClassName, to, exact, ...safeRest } = others;
  const listItemProps = isDomComponent ? { component, ...safeRest } : { component, ...others };

  return (
    <ListItem
      className={clsx('menu-vertical-collapse', {
        'rounded-menu': menuStyle === MenuStyle.ROUNDED,
        'rounded-menu-reverse': menuStyle === MenuStyle.ROUNDED_REVERSE,
        'standard-menu': menuStyle === MenuStyle.STANDARD,
        'curved-menu': menuStyle === MenuStyle.CURVED_MENU,
      })}
      sx={{
        height: 40,
        my: 0.25,
        cursor: 'pointer',
        textDecoration: 'none !important',
        mx: 2,
        width: 'calc(100% - 16px)',
        pl: 22 + 33 * level + 'px',
        pr: 3,
        borderRadius: 1,
        position: 'relative',
        transition: 'all 0.4s ease',
        whiteSpace: 'nowrap',
        '& .nav-item-text': {
          fontWeight: Fonts.MEDIUM,
          color: alpha(sidebarTextColor, 0.7),
          fontSize: 14,
        },

        '& .nav-item-icon': {
          color: alpha(sidebarTextColor, 0.7),
          fontSize: 24,
          width: 24,
          height: 24,
          display: 'block',
        },

        '& .nav-item-icon-arrow': {
          color: alpha(sidebarTextColor, 0.7),
        },

        '& .MuiIconButton-root': {
          mr: 3,
          padding: 0,
        },

        '& .MuiTouchRipple-root': {
          zIndex: 1,
        },

        '&.close:not(.child-selected), &.open:not(.child-selected)': {
          '& .nav-item-text': {
            color: alpha(sidebarTextColor, 0.7) + '!important',
            fontWeight: Fonts.MEDIUM,
          },
          '& .nav-item-icon': {
            color: alpha(sidebarTextColor, 0.2) + '!important',
          },
          '& .nav-item-icon-arrow': {
            color: alpha(sidebarTextColor, 0.7) + '!important',
          },
        },

        '&.child-selected': {
          '& .nav-item-text': {
            color: sidebarTextColor + '!important',
            fontWeight: Fonts.MEDIUM,
          },
          '& .nav-item-icon': {
            color: sidebarTextColor + '!important',
          },
          '& .nav-item-icon-arrow': {
            color: sidebarTextColor + '!important',
          },
        },

        '&:hover:not(.child-selected), &:focus:not(.child-selected)': {
          '& .nav-item-text': {
            fontWeight: Fonts.MEDIUM,
            color: sidebarTextColor,
          },
          '& .nav-item-icon': {
            color: sidebarTextColor,
          },
          '& .nav-item-icon-arrow': {
            color: sidebarTextColor,
          },
        },

        '&.child-selected:hover, &.child-selected:focus': {
          '& .nav-item-text': {
            color: sidebarTextColor + '!important',
            fontWeight: Fonts.MEDIUM,
          },
          '& .nav-item-icon': {
            color: sidebarTextColor + '!important',
          },
          '& .nav-item-icon-arrow': {
            color: sidebarTextColor + '!important',
          },
        },

        '&.leaf-menu.active': {
          backgroundColor: sidebarMenuSelectedBgColor + '!important',
          pointerEvents: 'none',
          '& .nav-item-text': {
            color: sidebarMenuSelectedTextColor + '!important',
            fontWeight: Fonts.MEDIUM,
          },
          '& .nav-item-icon': {
            color: sidebarMenuSelectedTextColor + '!important',
          },
        },
        '&.rounded-menu': {
          mr: 4,
          ml: 0,
          width: 'calc(100% - 16px)',
          pl: 30 + 33 * level + 'px',
          pr: 3,
          borderRadius: '0 30px 30px 0',
        },
        '&.rounded-menu-reverse': {
          ml: 4,
          mr: 0,
          width: 'calc(100% - 16px)',
          pl: 14 + 33 * level + 'px',
          pr: 3,
          borderRadius: '30px 0 0 30px',
        },
        '&.standard-menu': {
          mx: 0,
          width: '100%',
          pl: 30 + 33 * level + 'px',
          pr: 3,
          borderRadius: 0,
          position: 'relative',
          '&:after': {
            content: '""',
            position: 'absolute',
            right: 0,
            top: 0,
            height: '100%',
            width: 4,
            backgroundColor: 'transparent',
          },
          '&.leaf-menu.active:after': {
            backgroundColor: (theme) => theme.palette.primary.main,
          },
        },
        '&.curved-menu': {
          ml: 4,
          mr: 0,
          width: 'calc(100% - 16px)',
          pl: 14 + 33 * level + 'px',
          pr: 3,
          borderRadius: '30px 0 0 30px',
          position: 'relative',
          transition: 'none',
          '&:before, &:after': {
            content: '""',
            position: 'absolute',
            right: 0,
            zIndex: 1,
            height: 40,
            width: 40,
            backgroundColor: 'transparent',
            borderRadius: '50%',
            opacity: 0,
          },
          '&:before': {
            top: -40,
            boxShadow: `30px 30px 0 10px transparent`,
          },
          '&:after': {
            bottom: -40,
            boxShadow: `30px -30px 0 10px transparent`,
          },
          '&.leaf-menu.active': {
            backgroundColor: sidebarMenuSelectedBgColor,
            '& .nav-item-text, & .nav-item-icon': {
              color: sidebarMenuSelectedTextColor + '!important',
            },
            '&:before': {
              boxShadow: `30px 30px 0 10px ${sidebarMenuSelectedBgColor}`,
              opacity: 1,
            },
            '&:after': {
              boxShadow: `30px -30px 0 10px ${sidebarMenuSelectedBgColor}`,
              opacity: 1,
            },
          },
          '& .MuiTouchRipple-root': {
            display: 'none',
          },
        },
      }}
      {...listItemProps}
    >
      {children}
    </ListItem>
  );
};

export default VerticalCollapseItem;
